import { object, string } from "yup";
import { useEffect, useState } from "react";
import { useForm, useFormState } from "react-hook-form";

import Validations from "@src/utility/helpers/validations";
import { defaultValues } from "../misc/constants";
import size from "lodash/size";
import useAxios from "@src/utility/hooks/use-axios";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const useUpsert = (props) => {
    const { modalConfig:{ row }, handleModal, getAllNotes, isEdit } = props;
    const { axiosInstance, loading } = useAxios({ base: "panel" });
    const { axiosInstance:templateInstance, loading :templateLoading } = useAxios({ base: "panel", initialLoader: true });

    const { clientId, platformId } = useParams();
    const [selectedMacros, setSelectedMacros] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [templates, setTemplates] = useState({
        data      : [],
        pagination: {}
    });
    const { requiredValidation } = Validations();
    const methods = useForm({
        defaultValues,
        resolver: yupResolver(
            object({
                title: requiredValidation({ name: "Title" }),
                desc : requiredValidation({ name: "Description" }),
                id   : string()
            })
        )
    });
    const { reset, handleSubmit, setValue, watch, control } = methods;
    const { isDirty } = useFormState({ control });

    const getTemplates = async ({ page, limit }) => {
        const { data, pagination } = await templateInstance.get(`/notes/${platformId}/template`, {
            params: {
                page,
                limit
            }
        });
        setTemplates({
            data,
            pagination
        });    
    };
   
    const handlePagination = ({ page = 1, limit = 10 }) => {
        getTemplates({ page, limit });
    };
    
    const matchTemplateInDesc = (desc, template) => {
        const block = `${template.title}\n${template.content}`;
        return desc.includes(block);
    };

    useEffect(() => {
        if (size) {
            reset(row);
        } else {
            reset(defaultValues);
        }
        getTemplates({ page: 1, limit: 10 });
    }, [row]);

    useEffect(() => {
        const desc = watch("desc") || "";
        const matchedTemplate = templates.data.find(t => matchTemplateInDesc(desc, t));
        if (matchedTemplate) {
            setSelectedTemplateId(matchedTemplate._id);
        } else {
            setSelectedTemplateId(null);
        }
    }, [templates.data, watch("desc")]);

    const handleTemplateSelect = ({ _id, title, content }) => {
        const currentDesc = watch("desc") || "";
        let cleanedDesc = currentDesc;
        templates.data.forEach(template => {
            const block = `${template.title}\n${template.content}`;
            cleanedDesc = cleanedDesc.replace(block, "");
        });
        cleanedDesc = cleanedDesc.replace(/\n\s*\n/g, "\n").trim();
        const templateBlock = `${title}\n${content}`;
        const newDesc = cleanedDesc ? `${cleanedDesc}\n${templateBlock}` : templateBlock;
        setValue("desc", newDesc);
        setSelectedTemplateId(_id);
    };

    const onSubmit = () => {
        handleSubmit(async (formData) => {
            const payload = {
                title: formData.title,
                desc : formData.desc,
                ...(!isEdit ? { clientId } : {})
            };
            if (isEdit) {
                await axiosInstance.patch(`/notes/${platformId}/${row.id}`, payload);
            } else {
                await axiosInstance.post(`/notes/${platformId}`, payload);
            }
            getAllNotes({ page: 1, limit: 10 });
            handleModal();
        })();
    };
    
    const handleMacroSelect = (macroValue, itemKey) => {
        const macroLine = `${itemKey.toUpperCase()}: ${macroValue}`;        
        const isSelected = selectedMacros.includes(macroLine);

        if (isSelected) {
            setSelectedMacros((prev) => prev.filter((m) => m !== macroLine));
            const currentDesc = watch("desc") || "";
            const updatedDesc = currentDesc
                .split("\n")
                .filter((line) => line.trim() !== macroLine.trim())
                .join("\n");
            setValue("desc", updatedDesc);
        } else {
            setSelectedMacros((prev) => [...prev, macroLine]);
            const currentDesc = watch("desc") || "";
            const newLine = currentDesc ? `\n${macroLine}` : macroLine;
            setValue("desc", `${currentDesc}${newLine}`);
        }
    };

    return {
        loading,
        methods,
        onSubmit,
        selectedMacros,
        handleMacroSelect,
        handleTemplateSelect,
        isDirty,
        templates,
        templateLoading,
        handlePagination,
        selectedTemplateId
    };
};

export default useUpsert;